import {
    consentOriginStorage,
    consentReturnToStorage,
    setupPreviousStepStorage,
    setupTenantIdStorage
} from "@sg-protect/core";
import { type PropsWithChildren, createContext, useCallback, useContext, useMemo, useState } from "react";

// Mirrors the BFF’s ConsentOrigin. It parameterises tenant-assessment’s consent-request endpoint, which is
// outside the shell-bff spec, so the shell declares the values it routes setup on itself.
export const consentOriginValues = ["governance", "migrateAssessment"] as const;

export type ConsentOrigin = (typeof consentOriginValues)[number];

export interface ConsentHandoff {
    origin: ConsentOrigin | null;
    returnTo: string | null;
}

export interface SetupContextType extends ConsentHandoff {
    isGovernance: boolean;
    isMigrateAssessment: boolean;
    hasHandoff: boolean;
    setConsentHandoff: (handoff: ConsentHandoff) => void;
    clearSetupContext: () => void;
}

function isConsentOrigin(value: string | null): value is ConsentOrigin {
    return value !== null && (consentOriginValues as readonly string[]).includes(value);
}

export function parseConsentOrigin(value: string | null): ConsentOrigin | null {
    return isConsentOrigin(value) ? value : null;
}

function hasValue(value: string | null): boolean {
    return value !== null && value !== "";
}

export function clearSetupStorage() {
    consentOriginStorage.set(null);
    consentReturnToStorage.set(null);
    setupPreviousStepStorage.clear();
    setupTenantIdStorage.set(null);
}

const SetupContext = createContext<SetupContextType | null>(null);

export const useSetupContext = () => {
    const context = useContext(SetupContext);

    if (!context) {
        throw new Error("useSetupContext must be used within a SetupProvider");
    }

    return context;
};

export function SetupProvider({ children }: PropsWithChildren) {
    const [handoff, setHandoff] = useState<ConsentHandoff>(() => ({
        origin: parseConsentOrigin(consentOriginStorage.get()),
        returnTo: consentReturnToStorage.get()
    }));

    const setConsentHandoff = useCallback((next: ConsentHandoff) => {
        consentOriginStorage.set(next.origin);
        consentReturnToStorage.set(next.returnTo);
        setupPreviousStepStorage.clear();
        setHandoff(next);
    }, []);

    const clearSetupContext = useCallback(() => {
        clearSetupStorage();
        setHandoff({ origin: null, returnTo: null });
    }, []);

    const value = useMemo<SetupContextType>(
        () => ({
            ...handoff,
            isGovernance: handoff.origin === "governance" || handoff.origin === null,
            isMigrateAssessment: handoff.origin === "migrateAssessment",
            hasHandoff: hasValue(handoff.origin) || hasValue(handoff.returnTo),
            setConsentHandoff,
            clearSetupContext
        }),
        [handoff, setConsentHandoff, clearSetupContext]
    );

    return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
}
